// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import {
  GetMedicineCompanyDetail,
  CreateMedicineCompany,
  UpdateMedicineCompany,
} from "../../../services/MedicineCompanyService";
import { PreferencesContext } from "../../../PreferenceContext";

export default function MedicineCompanySave(Props: any) {
  // PROPS
  const { isForPopUp = false, handleClosePopUp, name } = Props;
  // SCHEMA FOR FORM
  interface formInitialValueTypes {
    medicineCompanyId: number;
    consumerId: number;
    orgId: number;
    branchId: number;
    code: string;
    medicineCompanyName: string;
    isLocked: boolean;
    isDeleted: boolean;
  }

  // DEFAULT SCHEMA VALUE FOR FORM
  const initMedicineCompany: formInitialValueTypes = {
    medicineCompanyId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    code: "",
    medicineCompanyName: isForPopUp ? name?.toUpperCase() : "",
    isLocked: false,
    isDeleted: false,
  };

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRavMedicineCompanyId = urlParams.get("q");
  let curMedicineCompanyId = 0;
  if (
    curRavMedicineCompanyId !== null &&
    curRavMedicineCompanyId !== undefined
  ) {
    let decId = edService.decrypt(curRavMedicineCompanyId);
    if (
      decId !== "" &&
      decId !== null &&
      decId !== undefined &&
      isForPopUp == false
    ) {
      curMedicineCompanyId = +decId;
    }
  }

  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    medicineCompanyId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    code: Yup.string(),
    medicineCompanyName: Yup.string().required(
      "Medicine Company Name is required"
    ),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initMedicineCompany, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  // GET Medicine Diluent Name DETAIL
  const getMedicineCompanyById = async () => {
    let MedicineCompany: any;
    let params = new URLSearchParams();
    params.append("MedicineCompanyId", curMedicineCompanyId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    try {
      const res: any = await GetMedicineCompanyDetail(params);
      MedicineCompany = res.data.data;
      methods.reset({
        ...initMedicineCompany,
        medicineCompanyId: MedicineCompany?.medicineCompanyId || 0,
        code: MedicineCompany?.code || "",
        medicineCompanyName: MedicineCompany?.medicineCompanyName || "",
        isLocked: MedicineCompany?.isLocked || false,
      });
      return MedicineCompany;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE MEDICINE COMPANY
  const saveMedicineCompany = async (value: formInitialValueTypes) => {
    try {
      let result: any = "";
      let formData = {
        medicineCompanyId: value.medicineCompanyId,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        code: value.code?.trim(),
        medicineCompanyName: value.medicineCompanyName?.trim(),
        isLocked: value.isLocked,
        isDeleted: value.isDeleted,
        DataManagedBy: userContext.loginDetail.userId,
      };
      if (curMedicineCompanyId > 0) {
        result = await UpdateMedicineCompany(formData);
      } else {
        result = await CreateMedicineCompany(formData);
      }
      if (result?.data) {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result.data) {
            handleClosePopUp("MEDICINE_COMPANY_CREATE", result.data);
          }
        }
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveMedicineCompany(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (!isForPopUp) {
      navigate(-1);
    } else {
      handleClosePopUp("CLOSE");
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curMedicineCompanyId > 0) {
        const MedicineCompany = await getMedicineCompanyById();
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curMedicineCompanyId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.code
                            ? "validation-message"
                            : ""
                        }`}>
                        Code
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input">
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("code")}
                          error={!!methods.formState.errors.code}
                          className={`text-input ${
                            methods.formState.errors.code ? "error" : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.code?.message}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    md={9}
                    display={{
                      xs: "none",
                      md: "grid",
                    }}></Grid> */}
                  <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.medicineCompanyName
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Medicine Company Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input"
                      >
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          autoFocus
                          type="text"
                          fullWidth
                          size="small"
                          {...methods.register("medicineCompanyName", {
                            onChange: (e) => {
                              const input = e.target;
                              const selectionStart = input.selectionStart;
                              const selectionEnd = input.selectionEnd;

                              // CONVERT INPUT INTO UPPERCASE LATTERS
                              e.target.value = input.value
                                ? input.value.toUpperCase()
                                : "";

                              // Restore the cursor position
                              setTimeout(() => {
                                input.setSelectionRange(
                                  selectionStart,
                                  selectionEnd
                                );
                              });

                              methods.setValue(
                                "medicineCompanyName",
                                e.target.value,
                                {
                                  shouldValidate: true,
                                }
                              ); // UPDATE VALUE
                            },
                          })}
                          error={!!methods.formState.errors.medicineCompanyName}
                          className={`text-input ${
                            methods.formState.errors.medicineCompanyName
                              ? "error"
                              : ""
                          }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.medicineCompanyName?.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton}
                  >
                    {curMedicineCompanyId > 0 ? "Update" : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}
                  >
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
